import { useState, type FocusEvent } from 'react';
import { normalizeWebsite } from '../utils/website';
import './WebsiteInput.css';

interface WebsiteInputProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
}

/**
 * The website box, everywhere one exists.
 *
 * The stored value carries no scheme (see `utils/website.ts`), so the control
 * shows a fixed `https://` beside it and normalizes what was pasted on blur —
 * paste `https://www.acme.com` and the box settles on `www.acme.com` rather
 * than reading "https://https://www.acme.com" until save silently reshapes it.
 *
 * Normalizing on BLUR, not on change: stripping as you type would fight
 * someone typing the scheme out by hand, deleting each character as it
 * arrives. Blur covers the paste case, because clicking Save blurs the field
 * first — and every caller normalizes again on submit for the keyboard path
 * that doesn't.
 */
export function WebsiteInput({
  value,
  onChange,
  disabled = false,
  placeholder = 'acme.com',
  id,
  'aria-label': ariaLabel,
}: WebsiteInputProps): JSX.Element {
  // Mirrors `value` while focused so a paste stays visible verbatim until the
  // field is left; the committed value is always the normalized one.
  const [buf, setBuf] = useState<string | null>(null);

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    setBuf(null);
    const next = normalizeWebsite(e.target.value);
    if (next !== value) onChange(next);
  };

  return (
    <div className="website-input">
      <span className="website-input-prefix" aria-hidden>https://</span>
      <input
        type="text"
        id={id}
        className="form-input"
        value={buf ?? value}
        onChange={(e) => {
          setBuf(e.target.value);
          onChange(e.target.value);
        }}
        onBlur={onBlur}
        disabled={disabled}
        maxLength={500}
        placeholder={placeholder}
        aria-label={ariaLabel}
        inputMode="url"
        autoCapitalize="none"
        spellCheck={false}
      />
    </div>
  );
}
